import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { FeedbackCategory, ClassSetup } from '../types/setup';
import { apiService } from '../services/apiService';

interface SetupState {
  currentStep: number;
  setup: ClassSetup;
  classId: string | null;
  loading: boolean;
  setCurrentStep: (step: number) => void;
  updateSetup: (updates: Partial<ClassSetup>) => void;
  toggleCategory: (categoryId: string) => void;
  setCategoryWeek: (categoryId: string, week: number) => void;
  setClassId: (classId: string) => void;
  saveSetup: () => Promise<void>;
  loadSetup: (classId: string) => Promise<void>;
}

const defaultCategories: FeedbackCategory[] = [
  {
    id: 'organization',
    name: 'Course Organization and Planning',
    description: 'Structure, objectives, and materials of the course',
    selected: false,
    week: 1,
  },
  {
    id: 'clarity',
    name: 'Clarity and Communication Skills',
    description: 'Effectiveness of instruction and explanation',
    selected: false,
    week: 1,
  },
  {
    id: 'interaction',
    name: 'Teacher-Student Interaction and Rapport',
    description: 'Engagement and relationship with students',
    selected: false,
    week: 1,
  },
  {
    id: 'difficulty',
    name: 'Course Difficulty and Workload',
    description: 'Balance and manageability of course demands',
    selected: false,
    week: 1,
  },
  {
    id: 'grading',
    name: 'Grading and Examinations',
    description: 'Assessment methods and feedback',
    selected: false,
    week: 1,
  },
  {
    id: 'learning',
    name: 'Student Self-Rated Learning',
    description: 'Student perception of their progress and understanding',
    selected: false,
    week: 1,
  },
];

const initialSetup: ClassSetup = {
  totalClasses: 10,
  frequency: 'weekly',
  feedbackType: 'exit',
  questionsPerSession: 3,
  selectedCategories: defaultCategories,
};

export const useSetupStore = create<SetupState>()(
  persist(
    (set, get) => ({
      currentStep: 0,
      setup: initialSetup,
      classId: null,
      loading: false,
      setCurrentStep: (step) => set({ currentStep: step }),
      updateSetup: (updates) =>
        set((state) => ({
          setup: { ...state.setup, ...updates },
        })),
      toggleCategory: (categoryId) =>
        set((state) => ({
          setup: {
            ...state.setup,
            selectedCategories: state.setup.selectedCategories.map((cat) =>
              cat.id === categoryId ? { ...cat, selected: !cat.selected } : cat
            ),
          },
        })),
      setCategoryWeek: (categoryId, week) =>
        set((state) => ({
          setup: {
            ...state.setup,
            selectedCategories: state.setup.selectedCategories.map((cat) =>
              cat.id === categoryId ? { ...cat, week } : cat
            ),
          },
        })),
      setClassId: (classId) => set({ classId }),
      saveSetup: async () => {
        const { classId, setup } = get();
        if (!classId) return;
        
        set({ loading: true });
        try {
          await apiService.saveClassSetup(classId, setup);
        } catch (error) {
          console.error('Failed to save setup:', error);
        } finally {
          set({ loading: false });
        }
      },
      loadSetup: async (classId: string) => {
        set({ loading: true, classId });
        try {
          const { data } = await apiService.getClassSetup(classId);
          if (data) {
            set({
              setup: {
                frequency: data.frequency as 'daily' | 'weekly' | 'biweekly',
                totalClasses: data.totalClasses,
                selectedCategories: data.selectedCategories as FeedbackCategory[],
                feedbackType: 'exit',
                questionsPerSession: 3,
              }
            });
          }
        } catch (error) {
          console.error('Failed to load setup:', error);
        } finally {
          set({ loading: false });
        }
      },
    }),
    {
      name: 'synced-setup-store',
      partialize: (state) => ({ 
        currentStep: state.currentStep, 
        setup: state.setup,
        classId: state.classId 
      }),
    }
  )
);